"use client";

import { useEffect, useRef, useState } from "react";
import { ChevronRight, Check } from "lucide-react";
import { getCurrencySymbol, DEFAULT_CURRENCY } from "@/lib/currencies";

/**
 * Slide-to-bid gesture component.
 *
 * Shows current price + a drag slider that triggers `onConfirm` with the bid amount
 * when the user drags past 85% of the track.
 *
 * Originally lived inline in AuctionFeedLayout (feed card). Extracted so it can be
 * reused on the listing detail page.
 */
export function SlideToBid({
  currentPrice,
  bidAmount,
  currency,
  disabled,
  onConfirm,
  trackLabel,
  confirmingLabel,
}: {
  /** Current highest bid (or start price) */
  currentPrice: number;
  /** Pre-calculated bid amount the slider commits to */
  bidAmount: number;
  currency?: string | null;
  /** When true the slider is inert (e.g. auction ended, user is seller) */
  disabled?: boolean;
  /** Fires once when the user completes the slide */
  onConfirm: (amount: number) => void;
  /** Replaces default “Slide to bid …” track hint when set */
  trackLabel?: string;
  /** Shown while the bid request is in flight after a successful slide */
  confirmingLabel?: string;
}) {
  const [dragX, setDragX] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [maxDrag, setMaxDrag] = useState(240);
  const containerRef = useRef<HTMLDivElement>(null);
  const startXRef = useRef(0);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const update = () => setMaxDrag(Math.max(0, el.clientWidth - 48));
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [disabled]);

  useEffect(() => {
    setDragX((x) => (maxDrag > 0 && x > maxDrag ? maxDrag : x));
  }, [maxDrag]);

  const handleStart = (clientX: number) => {
    if (confirmed || disabled) return;
    setIsDragging(true);
    startXRef.current = clientX - dragX;
  };

  const handleMove = (clientX: number) => {
    if (!isDragging || confirmed || disabled) return;
    const newX = Math.max(0, Math.min(clientX - startXRef.current, maxDrag));
    setDragX(newX);
  };

  const handleEnd = () => {
    if (!isDragging || confirmed || disabled) return;
    setIsDragging(false);
    if (dragX >= maxDrag * 0.85) {
      setDragX(maxDrag);
      setConfirmed(true);
      onConfirm(bidAmount);
      setTimeout(() => {
        setConfirmed(false);
        setDragX(0);
      }, 2000);
    } else {
      setDragX(0);
    }
  };

  const progress = maxDrag > 0 ? dragX / maxDrag : 0;
  const symbol = getCurrencySymbol(currency ?? DEFAULT_CURRENCY);

  return (
    <div
      ref={containerRef}
      className={`relative flex-1 min-w-0 h-12 rounded-full overflow-hidden transition-colors backdrop-blur-xl ${
        confirmed
          ? "bg-white/15 border-2 border-white"
          : "bg-black/40 border-2 border-white/15"
      }`}
      onMouseMove={(e) => handleMove(e.clientX)}
      onMouseUp={handleEnd}
      onMouseLeave={handleEnd}
      onTouchMove={(e) => handleMove(e.touches[0].clientX)}
      onTouchEnd={handleEnd}
    >
      <div
        className={`absolute inset-y-0 left-0 transition-colors ${confirmed ? "bg-white/20" : "bg-white/5"}`}
        style={{ width: `${progress * 100}%` }}
      />
      <div
        className="absolute inset-0 flex items-center justify-center pointer-events-none"
        style={{ opacity: 1 - progress * 1.5 }}
      >
        <span className={`font-mono text-xs sm:text-sm transition-colors truncate px-2 ${confirmed ? "text-white" : "text-neutral-500"}`}>
          {confirmed
            ? (confirmingLabel ?? "Opening…")
            : (trackLabel ?? `Slide to bid ${symbol}${bidAmount.toLocaleString()}`)}
        </span>
      </div>
      <div
        className="absolute top-1 bottom-1 left-1 w-10 rounded-full flex items-center justify-center cursor-grab active:cursor-grabbing transition-all select-none bg-white shrink-0"
        style={{
          transform: `translateX(${dragX}px)`,
          transition: isDragging ? "none" : "transform 0.3s ease-out",
        }}
        onMouseDown={(e) => handleStart(e.clientX)}
        onTouchStart={(e) => handleStart(e.touches[0].clientX)}
      >
        {confirmed ? (
          <Check className="h-4 w-4 text-black" />
        ) : (
          <ChevronRight className="h-4 w-4 text-black" />
        )}
      </div>
    </div>
  );
}
